/**
 * Board store - manages Kanban board state with API integration
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  boardsApi,
  tasksApi,
  type Board,
  type BoardDetail,
  type BoardColumn,
  type Task,
} from '@services/api';

// Monotonic sequence guarding board-scoped fetches. Fast board switching fires
// overlapping fetches; only the most recently initiated one is allowed to write
// state, so a slow earlier response cannot clobber the newer board's data.
let latestBoardFetchSeq = 0;

interface BoardState {
  // State
  boards: Board[];
  currentBoard: BoardDetail | null;
  tasks: Record<string, Task>;
  columnTaskIds: Record<string, string[]>;
  isLoading: boolean;
  error: string | null;
  selectedTaskId: string | null;

  // Actions
  fetchBoard: (boardId: string) => Promise<void>;
  fetchBoardTasks: (boardId: string) => Promise<void>;
  fetchBoardWithTasks: (boardId: string) => Promise<void>;
  createBoard: (data: {
    projectId: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
  }) => Promise<Board>;
  updateBoard: (boardId: string, data: Partial<Board>) => Promise<void>;
  deleteBoard: (boardId: string) => Promise<void>;
  /** Project boards are fetched without archived ones unless asked for. */
  fetchBoards: (projectId: string, includeArchived?: boolean) => Promise<void>;
  archiveBoard: (boardId: string) => Promise<void>;
  restoreBoard: (boardId: string) => Promise<void>;

  // Column actions
  addColumn: (boardId: string, data: { name: string; position?: number }) => Promise<void>;
  updateColumn: (
    boardId: string,
    columnId: string,
    data: { name?: string; wipLimit?: number; color?: string }
  ) => Promise<void>;
  deleteColumn: (boardId: string, columnId: string) => Promise<void>;
  reorderColumns: (boardId: string, columnOrder: string[]) => Promise<void>;

  // Task actions
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  removeTask: (taskId: string) => void;
  moveTask: (taskId: string, toColumnId: string, toPosition: number) => Promise<void>;

  // UI actions
  selectTask: (taskId: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

/**
 * Flip a board's archived flag optimistically, then reconcile with the server
 * payload (which carries archivedAt); roll back and rethrow on failure so the
 * caller can surface the reason (e.g. last active board of the project).
 */
async function applyArchiveState(
  boardId: string,
  archived: boolean,
  call: (boardId: string) => Promise<Board>
): Promise<void> {
  const previous = useBoardStore.getState().boards.find((b) => b.id === boardId);
  const previousCurrent = useBoardStore.getState().currentBoard;

  const patch = (state: BoardState, updates: Partial<Board>) => {
    const index = state.boards.findIndex((b) => b.id === boardId);
    if (index !== -1) state.boards[index] = { ...state.boards[index]!, ...updates };
    if (state.currentBoard?.id === boardId) {
      state.currentBoard = { ...state.currentBoard, ...updates };
    }
  };

  useBoardStore.setState((state) => {
    patch(state, { isArchived: archived, archivedAt: archived ? new Date().toISOString() : null });
  });

  try {
    const updated = await call(boardId);
    useBoardStore.setState((state) => {
      patch(state, { isArchived: updated.isArchived, archivedAt: updated.archivedAt });
    });
  } catch (error) {
    useBoardStore.setState((state) => {
      if (previous) patch(state, { isArchived: previous.isArchived, archivedAt: previous.archivedAt });
      if (previousCurrent && state.currentBoard?.id === boardId) state.currentBoard = previousCurrent;
    });
    throw error;
  }
}

export const useBoardStore = create<BoardState>()(
  immer((set, get) => ({
    // Initial state
    boards: [],
    currentBoard: null,
    tasks: {},
    columnTaskIds: {},
    isLoading: false,
    error: null,
    selectedTaskId: null,

    // Actions
    fetchBoards: async (projectId, includeArchived = false) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await boardsApi.list(projectId, includeArchived);
        set((state) => {
          // Spread to create mutable copy (API response may be frozen)
          state.boards = [...response.items];
          state.isLoading = false;
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to fetch boards';
        set((state) => {
          state.error = message;
          state.isLoading = false;
        });
        throw error;
      }
    },

    fetchBoard: async (boardId) => {
      const seq = ++latestBoardFetchSeq;
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const board = await boardsApi.get(boardId);
        // Bail if a newer board fetch superseded this one.
        if (seq !== latestBoardFetchSeq) return;
        set((state) => {
          state.currentBoard = board;
          // Initialize column task IDs
          state.columnTaskIds = {};
          board.columns.forEach((col) => {
            state.columnTaskIds[col.id] = [];
          });
          state.isLoading = false;
        });
      } catch (error: unknown) {
        if (seq !== latestBoardFetchSeq) return;
        const message = error instanceof Error ? error.message : 'Failed to fetch board';
        set((state) => {
          state.error = message;
          state.isLoading = false;
        });
        throw error;
      }
    },

    fetchBoardTasks: async (boardId) => {
      const seq = ++latestBoardFetchSeq;
      try {
        // Fetch all tasks by paginating through results (max page_size is 100)
        let allTasks: Task[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await tasksApi.list({ boardId }, page, 100);
          allTasks = allTasks.concat(response.items);
          hasMore = response.hasMore;
          page++;
        }

        const tasks = allTasks;

        // Bail if a newer board fetch superseded this one.
        if (seq !== latestBoardFetchSeq) return;

        set((state) => {
          // Reset tasks and column mappings
          state.tasks = {};
          state.columnTaskIds = {};

          // Initialize all columns from current board
          if (state.currentBoard) {
            state.currentBoard.columns.forEach((col: BoardColumn) => {
              state.columnTaskIds[col.id] = [];
            });
          }

          // Add tasks and build column mappings
          tasks.forEach((task) => {
            state.tasks[task.id] = task;
            if (!state.columnTaskIds[task.columnId]) {
              state.columnTaskIds[task.columnId] = [];
            }
            const colTasks = state.columnTaskIds[task.columnId];
            if (colTasks) {
              colTasks.push(task.id);
            }
          });

          // Sort tasks by position within each column
          Object.keys(state.columnTaskIds).forEach((columnId) => {
            const colTasks = state.columnTaskIds[columnId];
            if (colTasks) {
              colTasks.sort((a: string, b: string) => {
                const taskA = state.tasks[a];
                const taskB = state.tasks[b];
                return (taskA?.position ?? 0) - (taskB?.position ?? 0);
              });
            }
          });
        });
      } catch (error: unknown) {
        if (seq !== latestBoardFetchSeq) return;
        const message = error instanceof Error ? error.message : 'Failed to fetch tasks';
        set((state) => {
          state.error = message;
        });
        throw error;
      }
    },

    fetchBoardWithTasks: async (boardId) => {
      const seq = ++latestBoardFetchSeq;
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        // Fetch board and tasks in parallel for better performance
        const [board, tasksResponse] = await Promise.all([
          boardsApi.get(boardId),
          (async () => {
            let allTasks: Task[] = [];
            let page = 1;
            let hasMore = true;

            while (hasMore) {
              const response = await tasksApi.list({ boardId }, page, 100);
              allTasks = allTasks.concat(response.items);
              hasMore = response.hasMore;
              page++;
            }
            return allTasks;
          })(),
        ]);

        // Bail if a newer board fetch superseded this one.
        if (seq !== latestBoardFetchSeq) return;

        // Update state atomically - board and tasks together
        set((state) => {
          state.currentBoard = board;
          state.tasks = {};
          state.columnTaskIds = {};

          // Initialize all columns from the board
          board.columns.forEach((col) => {
            state.columnTaskIds[col.id] = [];
          });

          // Add tasks and build column mappings
          tasksResponse.forEach((task) => {
            state.tasks[task.id] = task;
            // Ensure column exists in columnTaskIds (handles orphaned tasks)
            if (!state.columnTaskIds[task.columnId]) {
              state.columnTaskIds[task.columnId] = [];
            }
            const colTasks = state.columnTaskIds[task.columnId];
            if (colTasks) {
              colTasks.push(task.id);
            }
          });

          // Sort tasks by position within each column
          Object.keys(state.columnTaskIds).forEach((columnId) => {
            const colTasks = state.columnTaskIds[columnId];
            if (colTasks) {
              colTasks.sort((a: string, b: string) => {
                const taskA = state.tasks[a];
                const taskB = state.tasks[b];
                return (taskA?.position ?? 0) - (taskB?.position ?? 0);
              });
            }
          });

          state.isLoading = false;
        });
      } catch (error: unknown) {
        if (seq !== latestBoardFetchSeq) return;
        const message = error instanceof Error ? error.message : 'Failed to fetch board';
        set((state) => {
          state.error = message;
          state.isLoading = false;
        });
        throw error;
      }
    },

    createBoard: async (data) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const board = await boardsApi.create(data);
        set((state) => {
          state.boards.push(board);
          state.isLoading = false;
        });
        return board;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to create board';
        set((state) => {
          state.error = message;
          state.isLoading = false;
        });
        throw error;
      }
    },

    updateBoard: async (boardId, data) => {
      const originalBoard = get().boards.find((b) => b.id === boardId);
      const originalCurrent = get().currentBoard;

      // Optimistic update
      set((state) => {
        const index = state.boards.findIndex((b: Board) => b.id === boardId);
        if (index !== -1) {
          const board = state.boards[index];
          if (board) {
            Object.assign(board, data);
          }
        }
        if (state.currentBoard?.id === boardId) {
          Object.assign(state.currentBoard, data);
        }
      });

      try {
        const updated = await boardsApi.update(boardId, data);
        set((state) => {
          const index = state.boards.findIndex((b: Board) => b.id === boardId);
          if (index !== -1) {
            state.boards[index] = updated;
          }
          if (state.currentBoard?.id === boardId) {
            state.currentBoard = { ...state.currentBoard, ...updated };
          }
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update board';
        // Rollback
        set((state) => {
          if (originalBoard) {
            const index = state.boards.findIndex((b: Board) => b.id === boardId);
            if (index !== -1) {
              state.boards[index] = originalBoard;
            }
          }
          if (originalCurrent) {
            state.currentBoard = originalCurrent;
          }
          state.error = message;
        });
        throw error;
      }
    },

    archiveBoard: async (boardId) => {
      await applyArchiveState(boardId, true, (id) => boardsApi.archive(id));
    },

    restoreBoard: async (boardId) => {
      await applyArchiveState(boardId, false, (id) => boardsApi.restore(id));
    },

    deleteBoard: async (boardId) => {
      const originalBoards = [...get().boards];

      set((state) => {
        state.boards = state.boards.filter((b: Board) => b.id !== boardId);
        if (state.currentBoard?.id === boardId) {
          state.currentBoard = null;
        }
      });

      try {
        await boardsApi.delete(boardId);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to delete board';
        set((state) => {
          state.boards = originalBoards;
          state.error = message;
        });
        throw error;
      }
    },

    // Column actions
    addColumn: async (boardId, data) => {
      try {
        const updated = await boardsApi.addColumn(boardId, data);
        set((state) => {
          if (state.currentBoard?.id === boardId) {
            state.currentBoard.columns = updated.columns;
          }
          // Initialize empty task list for new column
          const newColumn = updated.columns.find(
            (c) => !state.columnTaskIds[c.id]
          );
          if (newColumn) {
            state.columnTaskIds[newColumn.id] = [];
          }
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to add column';
        set((state) => {
          state.error = message;
        });
        throw error;
      }
    },

    updateColumn: async (boardId, columnId, data) => {
      const originalColumns = get().currentBoard?.columns;

      // Optimistic update
      set((state) => {
        if (state.currentBoard?.id === boardId) {
          const col = state.currentBoard.columns.find((c: BoardColumn) => c.id === columnId);
          if (col) {
            Object.assign(col, data);
          }
        }
      });

      try {
        const updated = await boardsApi.updateColumn(boardId, columnId, data);
        set((state) => {
          if (state.currentBoard?.id === boardId) {
            state.currentBoard.columns = updated.columns;
          }
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update column';
        set((state) => {
          if (state.currentBoard?.id === boardId && originalColumns) {
            state.currentBoard.columns = originalColumns;
          }
          state.error = message;
        });
        throw error;
      }
    },

    deleteColumn: async (boardId, columnId) => {
      const originalColumns = get().currentBoard?.columns;
      const originalColumnTaskIds = { ...get().columnTaskIds };

      set((state) => {
        if (state.currentBoard?.id === boardId) {
          state.currentBoard.columns = state.currentBoard.columns.filter(
            (c: BoardColumn) => c.id !== columnId
          );
        }
        delete state.columnTaskIds[columnId];
      });

      try {
        const updated = await boardsApi.deleteColumn(boardId, columnId);
        set((state) => {
          if (state.currentBoard?.id === boardId) {
            state.currentBoard.columns = updated.columns;
          }
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to delete column';
        set((state) => {
          if (state.currentBoard?.id === boardId && originalColumns) {
            state.currentBoard.columns = originalColumns;
          }
          state.columnTaskIds = originalColumnTaskIds;
          state.error = message;
        });
        throw error;
      }
    },

    reorderColumns: async (boardId, columnOrder) => {
      const originalColumns = get().currentBoard?.columns;

      // Optimistic update
      set((state) => {
        if (state.currentBoard?.id === boardId) {
          state.currentBoard.columns.sort((a: BoardColumn, b: BoardColumn) => {
            return columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id);
          });
          state.currentBoard.columns.forEach((col: BoardColumn, idx: number) => {
            col.position = idx;
          });
        }
      });

      try {
        const currentColumns = get().currentBoard?.columns ?? [];
        const newColumns: BoardColumn[] = columnOrder
          .map((id) => currentColumns.find((c: BoardColumn) => c.id === id))
          .filter((c): c is BoardColumn => !!c)
          .map((c, idx) => ({ ...c, position: idx }));

        // If any id in columnOrder no longer maps to a known column (e.g. a
        // column was deleted concurrently), the payload would be corrupt.
        // Roll back the optimistic reorder and refetch instead of sending it.
        if (newColumns.length !== columnOrder.length) {
          set((state) => {
            if (state.currentBoard?.id === boardId && originalColumns) {
              state.currentBoard.columns = originalColumns;
            }
          });
          await get().fetchBoard(boardId);
          return;
        }

        await boardsApi.updateColumns(boardId, newColumns);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to reorder columns';
        set((state) => {
          if (state.currentBoard?.id === boardId && originalColumns) {
            state.currentBoard.columns = originalColumns;
          }
          state.error = message;
        });
        throw error;
      }
    },

    // Task actions
    setTasks: (tasks) =>
      set((state) => {
        state.tasks = {};
        state.columnTaskIds = {};

        // Initialize all columns
        if (state.currentBoard) {
          state.currentBoard.columns.forEach((col: BoardColumn) => {
            state.columnTaskIds[col.id] = [];
          });
        }

        tasks.forEach((task: Task) => {
          state.tasks[task.id] = task;
          if (!state.columnTaskIds[task.columnId]) {
            state.columnTaskIds[task.columnId] = [];
          }
          const colTasks = state.columnTaskIds[task.columnId];
          if (colTasks) {
            colTasks.push(task.id);
          }
        });

        // Sort by position
        Object.keys(state.columnTaskIds).forEach((columnId) => {
          const colTasks = state.columnTaskIds[columnId];
          if (colTasks) {
            colTasks.sort((a: string, b: string) => {
              const taskA = state.tasks[a];
              const taskB = state.tasks[b];
              return (taskA?.position ?? 0) - (taskB?.position ?? 0);
            });
          }
        });
      }),

    addTask: (task) =>
      set((state) => {
        state.tasks[task.id] = task;
        if (!state.columnTaskIds[task.columnId]) {
          state.columnTaskIds[task.columnId] = [];
        }
        // Insert at correct position
        const colTasks = state.columnTaskIds[task.columnId];
        if (colTasks) {
          colTasks.splice(task.position, 0, task.id);
        }
      }),

    updateTask: (taskId, updates) =>
      set((state) => {
        if (state.tasks[taskId]) {
          Object.assign(state.tasks[taskId], updates);
        }
      }),

    removeTask: (taskId) =>
      set((state) => {
        const task = state.tasks[taskId];
        if (task) {
          const colTasks = state.columnTaskIds[task.columnId];
          if (colTasks) {
            const index = colTasks.indexOf(taskId);
            if (index > -1) {
              colTasks.splice(index, 1);
            }
          }
          delete state.tasks[taskId];
        }
      }),

    moveTask: async (taskId, toColumnId, toPosition) => {
      const task = get().tasks[taskId];
      if (!task) return;

      const originalColumnId = task.columnId;
      const originalPosition = task.position;

      // Optimistic update
      set((state) => {
        const task = state.tasks[taskId];
        if (!task) return;

        // Remove from old column
        const oldColTasks = state.columnTaskIds[task.columnId];
        if (oldColTasks) {
          const oldIndex = oldColTasks.indexOf(taskId);
          if (oldIndex > -1) {
            oldColTasks.splice(oldIndex, 1);
          }
        }

        // Add to new column
        if (!state.columnTaskIds[toColumnId]) {
          state.columnTaskIds[toColumnId] = [];
        }
        state.columnTaskIds[toColumnId].splice(toPosition, 0, taskId);

        // Update task
        task.columnId = toColumnId;
        task.position = toPosition;
      });

      try {
        await tasksApi.move(taskId, {
          columnId: toColumnId,
          position: toPosition,
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to move task';
        // Surgical rollback: move only this task back to its original column and
        // position, leaving other tasks' arrays untouched. Restoring a whole-map
        // snapshot would clobber concurrent successful moves made while this
        // request was in flight.
        set((state) => {
          const task = state.tasks[taskId];
          if (task) {
            // Remove from wherever it currently sits.
            const currentColTasks = state.columnTaskIds[task.columnId];
            if (currentColTasks) {
              const currentIndex = currentColTasks.indexOf(taskId);
              if (currentIndex > -1) {
                currentColTasks.splice(currentIndex, 1);
              }
            }

            // Re-insert into the original column at the original position.
            if (!state.columnTaskIds[originalColumnId]) {
              state.columnTaskIds[originalColumnId] = [];
            }
            const originalColTasks = state.columnTaskIds[originalColumnId];
            if (originalColTasks && !originalColTasks.includes(taskId)) {
              originalColTasks.splice(originalPosition, 0, taskId);
            }

            task.columnId = originalColumnId;
            task.position = originalPosition;
          }
          state.error = message;
        });
        throw error;
      }
    },

    // UI actions
    selectTask: (taskId) =>
      set((state) => {
        state.selectedTaskId = taskId;
      }),

    setLoading: (isLoading) =>
      set((state) => {
        state.isLoading = isLoading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
        state.isLoading = false;
      }),

    reset: () =>
      set((state) => {
        state.boards = [];
        state.currentBoard = null;
        state.tasks = {};
        state.columnTaskIds = {};
        state.isLoading = false;
        state.error = null;
        state.selectedTaskId = null;
      }),
  }))
);
